import { ConfirmationComponent } from '@components/confirmation/1.0';
import { DeleteKeyDialogComponent } from '@components/delete-key-dialog/delete-key-dialog.component';
import { first, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogData } from '@components/confirmation/1.0/index';
import { Subject } from 'rxjs';

export const brandMixin = (Base) =>
  class extends Base {
    isStatusChange = false;
    enableSuccess = false;
    disableSuccess = false;
    enableError = false;
    disableError = false;
    deleteApiKeyError = false;
    deleteApiKeySuccess = false;
    changedKeyId: string;
    isDeleteKey = false;
    deleteKeySuccess = false;
    deleteKeyError = false;

    isStatusChange$ = new Subject<boolean>();
    showApiKey$ = new Subject<boolean>();
    isDeleteKey$ = new Subject<boolean>();
    changeKeyId$ = new Subject<string>();

    onShow(row) {
      this.showApiKey = true;
      this.showApiKey$.next(true);
      this.changedKeyId = row?.id;
      this.changeKeyId$.next(row?.id);
      this.resetMessages();
      this.apiKeyService
        .getApiKeyById(row?.id, this.brandId)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (key: any) => {
            row.value = key?.value;
            this.changedKeyId = '';
            this.showApiKey = false;
            this.showApiKey$.next(false);
            this.changeKeyId$.next('');
          },
          error: (err) => {
            this.showLoadError = true;
            this.changedKeyId = '';
            this.showApiKey = false;
            this.showApiKey$.next(false);
            this.changeKeyId$.next('');
          },
        });
    }

    onStatusChange(event, key) {
      this.isStatusChange = true;
      this.isStatusChange$.next(true);
      this.changedKeyId = key.id;
      this.changeKeyId$.next(key.id);
      this.resetMessages();
      let modalContent = {
        title: this.translateService.instant('brand.apiKeys.statusChange'),
        message: this.translateService.instant('brand.apiKeys.statusChangeMsg', {
          change: key.enabled ? 'disable' : 'enable',
          desc: key.description,
        }),
        cancelText: this.translateService.instant('form.button.cancel'),
        confirmText: this.translateService.instant('form.button.continue'),
      };

      this.statusDialogRef = this.dialog.open(ConfirmationComponent, {
        data: modalContent,
      });

      this.statusDialogRef.afterClosed().subscribe((confirmAction: boolean) => {
        if (confirmAction && !this.statusChangeFromEditView) {
          this.apiKeyService
            .updateApiKey({ enabled: event.checked }, key.id, this.brandId)
            .pipe(first())
            .subscribe({
              next: () => {
                key.enabled = event.checked;
                this.isStatusChange = false;
                this.isStatusChange$.next(false);
                key.enabled ? (this.enableSuccess = true) : (this.disableSuccess = true);
                this.changedKeyId = '';
                this.changeKeyId$.next('');
              },
              error: (err) => {
                this.isStatusChange = false;
                this.isStatusChange$.next(false);
                key.enabled = !event.checked;
                key.enabled ? (this.enableError = true) : (this.disableError = true);
                this.changedKeyId = '';
                this.changeKeyId$.next('');
              },
            });
        } else if (confirmAction && this.statusChangeFromEditView) {
          key.enabled = !event.checked;
          this.isStatusChange = false;
          this.isStatusChange$.next(false);
        } else {
          this.isStatusChange = false;
          this.isStatusChange$.next(false);
          event.source.checked = key.enabled;
          this.form?.get('enabled')?.setValue(key.enabled);
          this.form?.controls['enabled']?.markAsPristine();
          this.changedKeyId = '';
          this.changeKeyId$.next('');
        }
      });
    }

    onDelete(key) {
      this.isDeleteKey = true;
      this.isDeleteKey$.next(true);
      this.changedKeyId = key.id;
      this.resetMessages();
      let modalContent: ConfirmationDialogData = {
        title: this.translateService.instant('brand.apiKeys.deleteKey'),
        message: this.translateService.instant('brand.apiKeys.deleteKeyMsg', {
          desc: key.description,
        }),
        inputLabel: this.translateService.instant('brand.apiKeys.deleteKey'),
        input: true,
        inputConfirmText: 'DELETE',
        cancelText: this.translateService.instant('form.button.no'),
        confirmText: this.translateService.instant('form.button.yes'),
      };

      this.deleteKeyDialogRef = this.dialog.open(DeleteKeyDialogComponent, {
        data: modalContent,
      });

      this.deleteKeyDialogRef.afterClosed().subscribe((confirmAction: boolean) => {
        if (confirmAction) {
          this.apiKeyService
            .deleteApiKey(key.id, this.brandId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
              next: () => {
                this.isDeleteKey = false;
                this.isDeleteKey$.next(false);
                this.deleteKeySuccess = true;
                this.changedKeyId = '';
                this.changeKeyId$.next('');
                if (this.statusChangeFromEditView) {
                  this.router.navigate(['../'], { relativeTo: this.route });
                } else {
                  this.apiKeys =
                    this.apiKeys?.length > 1 ? this.apiKeys?.filter((k) => k.id !== key.id) : [];
                  this.dataSource.data = Object.assign([], this.apiKeys);
                }
              },
              error: (err) => {
                this.isDeleteKey = false;
                this.isDeleteKey$.next(false);
                this.deleteKeyError = true;
                this.changedKeyId = '';
                this.changeKeyId$.next('');
              },
            });
        } else {
          this.isDeleteKey = false;
          this.isDeleteKey$.next(false);
          this.changedKeyId = '';
          this.changeKeyId$.next('');
        }
      });
    }

    resetMessages() {
      this.showLoadError = false;
      this.disableError = false;
      this.enableError = false;
      this.enableSuccess = false;
      this.disableSuccess = false;
      this.deleteApiKeyError = false;
      this.deleteApiKeySuccess = false;
      this.addKeySuccess = false;
      this.addKeyError = false;
      this.updateKeySuccess = false;
      this.updateKeyError = false;
    }
  };
